import {FormControl, FormLabel, MenuItem, Select} from '@mui/material'
import {ImageProcessStatusEnum, meterReadEnum, serverCycleLabels} from 'utils/const/globalConst'

/**
 * 선택값을 콤마로 이어붙인 문자열로 보관하는 다중선택 필터.
 * (그 외 다중선택은 배열 그대로 filter 에 넣는다)
 */
const commaJoinedMultiIds = ['searchDataField', 'processStatus', 'diameters', 'serverCycle']

interface CustomSelectBoxProps {
  id: string
  label: string
  filter: Record<string, any>
  setFilter: (val: Record<string, any>) => void
  multiple?: boolean
  options?: Record<string, string> // key-value enum 객체도 가능
  externalFilter?: any
  compact?: boolean
}

export const CustomSelectBox = ({
  id,
  label,
  filter,
  setFilter,
  multiple = false,
  options,
  externalFilter,
  compact = false,
}: CustomSelectBoxProps) => {
  const formControlWidthHandler = (id: string) => {
    switch (id) {
      case 'searchDataField':
      case 'searchDataFieldBinaryCheck':
        return 'md:w-[20rem]'
      case 'processStatus':
        return 'md:w-[18rem]'
      case 'recognizedType':
        return 'md:w-[16rem]'
      case 'compareType':
        return 'md:w-[16rem]'

      default:
        return 'md:w-[14rem]'
    }
  }

  const defaultMenuHandler = (id: string, label: string) => {
    switch (id) {
      case 'searchDataField':
        return [
          {value: 'customerName', name: '수용가이름'},
          {value: 'customerNumber', name: '수용가번호'},
          {value: 'serialNumber', name: '단말기 일련번호'},
          {value: 'address', name: '주소'},
          {value: 'managerName', name: '담당자'},
          {value: 'installerName', name: '설치자'},
          {value: 'memo', name: '메모'},
        ]

      case 'searchDataFieldBinaryCheck':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {value: 'customerName', name: '수용가이름'},
          {value: 'customerNumber', name: '수용가번호'},
          {value: 'address', name: '주소'},
          {value: 'managerName', name: '담당자'},
          {value: 'installerName', name: '설치자'},
          {value: 'memo', name: '메모'},
        ]

      case 'deviceStatus':
      case 'batteryStatus':
      case 'efosStatus':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {
            value: 'true',
            name: '정상',
          },
          {
            value: 'false',
            name: '점검',
          },
        ]
      case 'processStatus':
        return [
          {value: 'COMPLETE', name: ImageProcessStatusEnum.COMPLETE},
          {value: 'FIX', name: ImageProcessStatusEnum.FIX},
          {value: 'DIGIT', name: ImageProcessStatusEnum.DIGIT},
          {value: 'FAIL', name: ImageProcessStatusEnum.FAIL},
          {value: 'IMGERR', name: ImageProcessStatusEnum.IMGERR},
          {value: 'DANGER', name: ImageProcessStatusEnum.DANGER},
          {value: 'WARNING', name: ImageProcessStatusEnum.WARNING},
        ]

      case 'deviceType':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {
            value: 'analog',
            name: '디지털 이미지',
          },
          {
            value: 'digital',
            name: '디지털 계량기',
          },
        ]
      case 'recognizedType':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {
            value: 'analog',
            name: meterReadEnum.ANALOG,
          },
          {
            value: 'digital',
            name: meterReadEnum.DIGITAL,
          },
          {
            value: 'gas',
            name: meterReadEnum.GAS,
          },
          {
            value: 'PRV',
            name: meterReadEnum.PRV,
          },
        ]
      case 'isRevised':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {
            value: 'true',
            name: '보정',
          },
          {
            value: 'false',
            name: '미보정',
          },
        ]
      case 'signalStrength':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {
            value: 'normal',
            name: '정상(-90이상)',
          },
          {
            value: 'abnormal',
            name: '낮음(-90미만)',
          },
        ]
      case 'temperature':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {
            value: 'normal',
            name: '양호(-1°C 초과)',
          },
          {
            value: 'warning',
            name: '주의(-1°C ~ -3°C)',
          },
          {
            value: 'dangerous',
            name: '위험(-3°C ~ -5°C)',
          },
          {
            value: 'very-dangerous',
            name: '매우 위험(-5°C 미만)',
          },
          {
            value: 'no_temperature',
            name: '온도 확인 불가',
          },
        ]
      case 'assignStatus':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {
            value: 'assigned',
            name: '할당',
          },
          {
            value: 'unassigned',
            name: '미할당',
          },
        ]
      case 'diameters':
        return externalFilter.diameter
      case 'compareType':
        return [
          {
            value: 'all',
            name: '전체',
          },
          {
            value: 'same',
            name: '같은 이미지',
          },
          {
            value: 'different',
            name: '다른 이미지',
          },
        ]

      case 'customerStatus':
        switch (label) {
          case '설치가능 여부':
            return [
              {
                value: 'all',
                name: '전체',
              },
              {
                value: 'true',
                name: '가능',
              },
              {
                value: 'false',
                name: '불가능',
              },
            ]
          case '수용가 상태':
            return [
              {
                value: 'all',
                name: '전체',
              },
              {
                value: 'normal',
                name: '정상',
              },
              {
                value: 'as',
                name: 'A/S 접수',
              },
              {
                value: 'remove',
                name: '철거',
              },
              {
                value: 'disused',
                name: '폐전',
              },
            ]

          default:
            return [
              {
                value: 'all',
                name: '전체',
              },
            ]
        }

      default:
        return []
    }
  }

  const menuList = options
    ? Object.entries(options).map(([key, name]) => ({value: key, name}))
    : defaultMenuHandler(id, label)

  const raw = filter?.[id]
  const value = multiple
    ? !raw || raw === ''
      ? commaJoinedMultiIds.includes(id)
        ? []
        : ['all']
      : raw.split(',').filter((v: string) => v !== '')
    : raw || 'all'

  const handleChange = (e: any, id: any) => {
    let selectedValues = e.target.value
    if (commaJoinedMultiIds.includes(id)) {
      if (selectedValues.includes('all')) {
        selectedValues = ['']
      } else {
        selectedValues = selectedValues.filter((v: any) => v !== 'all')
      }
      setFilter({
        ...filter,
        [id]: selectedValues.length > 0 ? selectedValues.join(',') : '',
      })
    } else {
      setFilter({
        ...filter,
        [id]: selectedValues,
      })
    }
  }

  const getRenderValue = (selected: any) => {
    if (!commaJoinedMultiIds.includes(id)) {
      if (Array.isArray(selected)) {
        const renderedValue = selected
          .map((val: string) => menuList.find((m: any) => m.value === val)?.name ?? val)
          .join(', ')
        return renderedValue === 'all' ? '전체' : renderedValue
      }
      return menuList.find((m: any) => m.value === selected)?.name ?? selected
    }

    // id === 'searchDataField'인 경우
    if (Array.isArray(selected)) {
      const renderedValue = selected
        .map((val: string) => {
          const label = menuList.find((m: any) => m.value === val)?.name ?? val
          return label
        })
        .join(', ')
      return renderedValue === '' ? '전체' : renderedValue
    }

    return ''
  }

  return (
    <FormControl className={`w-full ${formControlWidthHandler(id)}`} id={id}>
      <FormLabel sx={{fontSize: compact ? '0.875rem' : '1rem', marginBottom: '0.25rem'}}>
        {label}
      </FormLabel>
      <Select
        id={id}
        variant='outlined'
        autoComplete='off'
        multiple={multiple}
        value={value}
        onChange={(e) => {
          handleChange(e, id)
        }}
        displayEmpty
        sx={{
          fontSize: compact ? '1.125rem' : '1.75rem',
          height: compact ? '2.75rem' : '3.5rem',
          width: '100%',
          backgroundColor: 'white',
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            padding: compact ? '0.5rem 2rem 0.5rem 0.5rem' : undefined,
          },
        }}
        renderValue={getRenderValue}
      >
        {multiple && (
          <MenuItem value='all' sx={{fontSize: compact ? '1.125rem' : undefined}}>
            전체
          </MenuItem>
        )}

        {menuList.map(({value, name}: any) => {
          const selectedValues = Array.isArray(filter[id])
            ? filter[id]
            : filter[id]?.split(',') ?? []
          const index = selectedValues.indexOf(value)

          return (
            <MenuItem key={value} value={value} sx={{fontSize: compact ? '1.125rem' : undefined}}>
              {id === 'searchDataField' && index >= 0 ? `${index + 1}. ` : ''}
              {name}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}
